export function useIndexedDB(databaseName, storeName, keyPath, allowMetaData = false, storeList = []) {
  const openIndexedDB = () => {
    return new Promise((resolve, reject) => {
      try {
        const request = window.indexedDB.open(databaseName, 1)

        request.onupgradeneeded = event => {
          const db = event.target.result

          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, keyPath ? keyPath : { keyPath: "id", autoIncrement: true }) // key path and autoIncrement will expose the id the index DB is using
          }
          if (allowMetaData && !db.objectStoreNames.contains(`${storeName}Metadata`)) {
            db.createObjectStore(`${storeName}Metadata`, keyPath ? keyPath : { keyPath: "id", autoIncrement: true })
          }
          // SAFE LIMITATION TO CREATE ONLY 100 stores
          if (storeList?.length <= 100) {
            storeList?.forEach(item => {
              db.createObjectStore(`${item?.storeName}`, keyPath ? keyPath : { keyPath: "id", autoIncrement: true })
            })
          }
        }
        request.onsuccess = () => {
          resolve(request.result)
        }
        request.onerror = () => {
          console.error("Error while adding data transaction")
          reject("Error occurred while failing")
        }
      } catch (error) {
        throw new Error(`Something went wrong while creating the store  ${error}`)
      }
    })
  }

  const addDataIntoStore = async (value, optionalStoreName = storeName) => {
    try {
      const db = await openIndexedDB()
      const dbTransaction = db.transaction([optionalStoreName], "readwrite")
      const store = dbTransaction.objectStore(optionalStoreName)
      store.add(value)
      dbTransaction.onerror = () => console.error("Error while adding data transaction")
      dbTransaction.oncomplete = () => db.close()
    } catch (error) {
      throw new Error(`Something went wrong while adding data into store  ${error}`)
    }
  }

  const getAllDataFromStore = (value, optionalStoreName = storeName) => {
    return new Promise(async (resolve, reject) => {
      try {
        const db = await openIndexedDB()
        const dbTransaction = db.transaction([optionalStoreName], "readonly")
        const store = dbTransaction.objectStore(optionalStoreName)
        const request = store.getAll()

        request.onsuccess = event => resolve(event.target.result)
        request.onerror = event => {
          reject("Error retrieving data from IndexedDB: " + event.target.error)
          console.error("Error while getting all data from the transaction")
        }

        dbTransaction.oncomplete = () => db.close()
      } catch (error) {
        console.error("Error in getDataAllDataFromStore:", error)
        reject("Failed to retrieve data from store")
      }
    })
  }
  const deleteSingleDataEntryFromStore = async (id, optionalStoreName = storeName) => {
    try {
      const db = await openIndexedDB()
      const dbTransaction = db.transaction([optionalStoreName], "readwrite")
      const store = dbTransaction.objectStore(optionalStoreName)
      store.delete(id)
      dbTransaction.onerror = () => console.error("Error while deleting through transaction")
      dbTransaction.oncomplete = () => db.close()
    } catch (error) {
      throw new Error(`Something went wrong while deleting data from the store ${error}`)
    }
  }

  const deleteIndexedDB = dbName => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName)

      request.onsuccess = () => {
        console.log(`Database '${dbName}' deleted successfully.`)
        resolve()
      }

      request.onerror = event => {
        console.error(`Error deleting database '${dbName}':`, event.target.error)
        reject(event.target.error)
      }

      request.onblocked = () => {
        console.warn(`Database '${dbName}' deletion is blocked. Close all connections.`)
      }
    })
  }

  const editDataInStore = async (value, id, optionalStoreName = storeName) => {
    return new Promise(async (resolve, reject) => {
      try {
        const db = await openIndexedDB()
        const dbTransaction = db.transaction([optionalStoreName], "readwrite")
        const store = dbTransaction.objectStore(optionalStoreName)
        const request = id ? store.put(value, id) : store.put(value)

        request.onerror = () => reject("Failed to update record in store")

        dbTransaction.onerror = () => reject("Failed to update to store")
        dbTransaction.oncomplete = () => {
          resolve("Updation successfull")
          db.close()
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  return {
    addDataIntoStore,
    getAllDataFromStore,
    deleteSingleDataEntryFromStore,
    deleteIndexedDB,
    editDataInStore,
  }
}
